import { parseLocalDate } from "@/lib/date-utils";
import type { ReportAggregation } from "@glorify-llc/accounting-core";

// Per supabase/migrations/20260430120600_create_sacramental_records.sql, the
// CHECK constraint allows: baptism, confirmation, first_communion, wedding,
// marriage, funeral, ordination, other. The previous union was missing
// `first_communion` and `marriage`, which combined with the wiring's `as any[]`
// cast meant those rows quietly type-erased and produced sloppy display.
export type SacramentType =
  | "baptism"
  | "confirmation"
  | "first_communion"
  | "wedding"
  | "marriage"
  | "funeral"
  | "ordination"
  | "other";

export interface SacramentalRecordRow {
  id: string;
  person_name: string;
  person_id: string;
  // Typed loosely to match the DB schema (`text` with a CHECK constraint). The
  // adapter normalises before keying summaries/buckets so out-of-band values
  // don't silently disappear from "Total Records".
  sacrament_type: string;
  performed_on: string | null;
  officiant_name: string | null;
  location: string | null;
  notes: string | null;
}

export type SacramentalRecordsContext = {
  asOf: Date;
  records: SacramentalRecordRow[];
};

const EM_DASH = "—";

/**
 * Lower-case + trim a sacrament_type for case-insensitive bucketing. The DB
 * CHECK constraint enforces lowercase today, but seed data, manual SQL
 * inserts, or a future loosening of the constraint can bypass it — so the
 * adapter shouldn't trust casing. (Pattern: PR #1539, #1547, #1555, #1559.)
 */
function normalizeSacramentType(value: unknown): string {
  if (typeof value !== "string") return "other";
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : "other";
}

/**
 * Render a sacrament_type for display: replace underscores with spaces and
 * Title-Case each word. The previous `charAt(0).toUpperCase()` only capitalised
 * the first character, so `first_communion` rendered as "First_communion" and
 * an upstream `"BAPTISM"` rendered as "BAPTISM".
 */
function formatSacramentTypeLabel(value: string): string {
  if (value.length === 0) return "Other";
  return value
    .split("_")
    .map((part) =>
      part.length === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
    )
    .join(" ");
}

/**
 * Fall back to a placeholder when the upstream string is null OR empty/whitespace.
 * The previous `?? "—"` only caught null/undefined; an empty string from a
 * trimmed officiant field rendered as a blank cell.
 */
function withFallback(value: string | null | undefined, fallback = EM_DASH): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

/** Render the person label, falling back so a blank label never reaches the UI. */
function formatPersonName(value: unknown): string {
  if (typeof value !== "string") return "Unknown Person";
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : "Unknown Person";
}

/**
 * Validate the performed_on date. Sacramental dates are legally significant —
 * baptism, marriage, ordination records can be referenced years later for
 * canonical or civil purposes — so an invalid/empty date should render as
 * "Unknown" rather than propagating an unparseable string into a printed
 * report. Uses parseLocalDate so YYYY-MM-DD doesn't drift across timezones.
 */
function formatCeremonyDate(raw: string | null | undefined): string {
  if (typeof raw !== "string" || raw.trim().length === 0) return "Unknown";
  const parsed = parseLocalDate(raw);
  if (!parsed) return "Unknown";
  // Preserve the YYYY-MM-DD shape for callers that expect a sortable string,
  // but only after we've confirmed parseability.
  return raw.trim();
}

export function buildSacramentalRecordsAggregation(
  context: SacramentalRecordsContext,
): ReportAggregation {
  const records = Array.isArray(context.records) ? context.records : [];

  const rows = records.map((r) => {
    const normalizedType = normalizeSacramentType(r.sacrament_type);
    return {
      id: r.id,
      label: formatPersonName(r.person_name),
      entityType: "person" as const,
      drilldownUrl: `/people/${encodeURIComponent(r.person_id)}`,
      recordType: formatSacramentTypeLabel(normalizedType),
      ceremonyDate: formatCeremonyDate(r.performed_on),
      officiant: withFallback(r.officiant_name),
      location: withFallback(r.location),
    };
  });

  // Bucket by normalised type so casing/whitespace can't silently drop a row
  // from its summary count.
  const byType = records.reduce<Record<string, number>>((acc, r) => {
    const key = normalizeSacramentType(r.sacrament_type);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // The DB allows both `wedding` and `marriage` — treat them as the same
  // human-facing concept ("Marriages") so the summary doesn't undercount when
  // staff use them interchangeably.
  const marriagesCount = (byType["wedding"] ?? 0) + (byType["marriage"] ?? 0);

  return {
    asOf: context.asOf,
    summary: [
      { label: "Total Records", value: records.length },
      { label: "Baptisms", value: byType["baptism"] ?? 0 },
      { label: "Confirmations", value: byType["confirmation"] ?? 0 },
      { label: "Marriages", value: marriagesCount },
    ],
    rows,
  };
}
